import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { IQuestion } from '../model/home';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

// interface Question {
//   id: string;
//   text: string;
//   hasImage?: boolean;
//   imageUrl?: string;
// }

interface MultiQuestionDialogProps {
  open: boolean;
  onClose: () => void;
  questions: IQuestion[];
  onGenerate: (selectedQuestions: IQuestion[]) => void;
  imageUrl?: string;
}

export function MultiQuestionDialog({
  open,
  onClose,
  questions,
  onGenerate,
  imageUrl,
}: MultiQuestionDialogProps) {
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(true);

  // useEffect(() => {
  //   setSelectedQuestions(new Set(questions.map((q) => q.id)));
  // }, []);
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const toggleQuestion = (questionId: string) => {
    setSelectedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleGenerate = () => {
    const selected = questions
      .filter((q) => selectedQuestions.has(q.id))
      .sort((a, b) => a.idx - b.idx);
    onGenerate(selected);
    // onClose();
  };

  const totalPoints = selectedQuestions.size * 10;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="p-0 overflow-hidden rounded-4xl border-2 border-black/10 max-w-none!"
        style={{ width: '1065px', maxHeight: '632px' }}
      >
        {/* Accessibility elements - visually hidden */}
        <DialogTitle className="sr-only">Select Questions</DialogTitle>
        <DialogDescription className="sr-only">
          Multiple questions included. Select the ones you want to create videos
          for by checking the boxes!
        </DialogDescription>

        {/* Scrollable Content */}
        <div className="overflow-y-auto h-[632px] px-12 py-10">
          {/* Header Text - 写死的文案 */}
          <div className="mb-8">
            <h3
              className="text-[18px] leading-tight text-black"
              style={{ fontFamily: 'PingFang SC, sans-serif' }}
            >
              Multiple questions included. Select the ones you want to create
              videos for by checking the boxes!
            </h3>
          </div>

          {/* Questions Grid - 一行两道题 */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {questions.map((question, index) => {
              const isSelected = selectedQuestions.has(question.id);

              return (
                <button
                  key={question.id}
                  onClick={() => toggleQuestion(question.id)}
                  className={`rounded-3xl p-6 overflow-auto transition-all text-left ${
                    isSelected
                      ? 'border-[1.5px] border-black bg-white'
                      : 'border-[1.5px]'
                  }`}
                  style={{
                    width: '464px',
                    height: '191px',
                    backgroundColor: isSelected
                      ? '#FFFFFF'
                      : 'rgba(0, 0, 0, 0.03)',
                    borderColor: isSelected ? '#FFE75C' : '',
                  }}
                >
                  {/* Question Text with number */}
                  <p className="text-[16px] leading-relaxed text-black mb-4">
                    {/* {index + 1}. {question.text} */}
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]} // 解析数学公式
                      rehypePlugins={[rehypeKatex]} // 渲染数学公式
                    >
                      {`${index + 1}.` + question.text}
                    </ReactMarkdown>
                  </p>

                  {/* Question Image if available */}
                  {question.imageUrl && (
                    <div className="rounded-2xl overflow-hidden bg-[#D9D9D9] flex items-center justify-center h-20">
                      {question.imageUrl &&
                      question.imageUrl !== 'placeholder' ? (
                        <img
                          src={question.imageUrl}
                          alt={`Question ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-sm">图片</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={selectedQuestions.size === 0}
            className="w-full h-14 rounded-[28px] bg-[#2C2C2C] hover:bg-[#2C2C2C]/90 text-[#FFE75C] text-[16px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Generate {selectedQuestions.size > 0 ? selectedQuestions.size : ''}{' '}
            Learning Clip{selectedQuestions.size > 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
